import { useState, useEffect } from 'react'
import { apiBase } from './lib/api'
import { useNavigate, Link } from 'react-router-dom'
import PlanCards from './PlanCards'
import { IconHeart } from './components/ui/Icons'

const API = apiBase
const PRODUCT = 'Manabi'

// Ojito para ver/ocultar la contraseña mientras se escribe
const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
)
const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
)
const eyeBtn = { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', alignItems: 'center' }

export default function AuthPage({ mode = 'login' }) {
  const navigate = useNavigate()
  // Con la sesión ya iniciada no tiene sentido el formulario: directo al Resumen
  useEffect(() => {
    if (localStorage.getItem('polar_token')) navigate('/resumen', { replace: true })
  }, [navigate])
  const [showPw, setShowPw]           = useState(false)
  const [form, setForm]               = useState({ email: '', password: '', name: '', phone: '' })
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [error, setError]             = useState(null)
  const [loading, setLoading]         = useState(false)
  const [loadingTrial, setLoadingTrial]   = useState(false)
  const [loadingDemo,  setLoadingDemo]    = useState(false)
  const [loadingFree,  setLoadingFree]    = useState(false)
  const [regStep, setRegStep]         = useState('form')  // 'form' | 'choose'
  const isRegister = mode === 'register'

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  // ── helpers ──────────────────────────────────────────────────────────────────
  function saveAndGo(token, user) {
    localStorage.setItem('polar_token', token)
    localStorage.setItem('polar_user', JSON.stringify(user))
    navigate('/resumen')
  }

  // ── Paso 1 → 2 del registro: validar datos y mostrar los planes ──────────────
  const goChoosePlan = () => {
    setError(null)
    if (!acceptedTerms) { setError('Debés aceptar los términos y condiciones para continuar.'); return }
    if (!form.name.trim() || !form.email.trim() || !form.password) { setError('Completá nombre, email y contraseña.'); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) { setError('Ingresá un email válido.'); return }
    if (form.password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return }
    setRegStep('choose')
  }

  // ── Login / registro pago (MercadoPago) ───────────────────────────────────────
  const submit = async e => {
    e?.preventDefault?.()
    setLoading(true); setError(null)
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, password: form.password }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      saveAndGo(data.token, data.user)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  const paidRegister = async (plan) => {
    setLoading(true); setError(null)
    try {
      const res = await fetch(`${API}/auth/start-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, password: form.password, phone: form.phone, plan }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      if (data.skipPayment) {
        saveAndGo(data.token, data.user)
      } else {
        window.location.href = data.checkoutUrl
      }
    } catch (err) {
      setError(err.message)
      setLoading(false)
    }
  }

  // ── Elegir plan en el paso 2 ──────────────────────────────────────────────────
  const [loadingPlan, setLoadingPlan] = useState(null)
  const choosePlan = async (plan) => {
    setLoadingPlan(plan)
    if (plan === 'free') await createFree()
    else await paidRegister(plan)
    setLoadingPlan(null)
  }

  // ── 3-day trial (real account, full access, no payment) ──────────────────────
  const startTrial = async () => {
    if (!acceptedTerms) { setError('Aceptá los términos y condiciones para continuar.'); return }
    if (!form.name || !form.email || !form.password) { setError('Completá nombre, email y contraseña primero.'); return }
    if (form.password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return }
    setLoadingTrial(true); setError(null)
    try {
      const res = await fetch(`${API}/auth/start-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, password: form.password }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      saveAndGo(data.token, data.user)
    } catch (err) {
      setError(err.message)
    }
    setLoadingTrial(false)
  }

  // ── Free account (permanent, no card) ────────────────────────────────────────
  const createFree = async () => {
    if (!acceptedTerms) { setError('Aceptá los términos y condiciones para continuar.'); return }
    if (!form.name || !form.email || !form.password) { setError('Completá nombre, email y contraseña primero.'); return }
    if (form.password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return }
    setLoadingFree(true); setError(null)
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, password: form.password }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      saveAndGo(data.token, data.user)
    } catch (err) {
      setError(err.message)
    }
    setLoadingFree(false)
  }

  // ── Instant demo (fake data, no account) ─────────────────────────────────────
  const startDemo = async () => {
    setLoadingDemo(true); setError(null)
    try {
      const res = await fetch(`${API}/auth/instant-demo`, { method: 'POST' })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      saveAndGo(data.token, data.user)
    } catch (err) {
      setError(err.message)
    }
    setLoadingDemo(false)
  }

  // ── Styles ───────────────────────────────────────────────────────────────────
  const inputStyle = {
    width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12, padding: '13px 16px', fontSize: 15, color: '#fff',
    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
  }

  const btnPrimary = (disabled) => ({
    background: '#ff3b30', border: 'none', color: '#fff', fontSize: 15, fontWeight: 700,
    borderRadius: 12, padding: '14px', cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.7 : 1, fontFamily: 'inherit', width: '100%',
  })

  return (
    <div style={{
      minHeight: '100vh', background: '#0a0a0f', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px 16px', fontFamily: '-apple-system,BlinkMacSystemFont,"SF Pro Display",sans-serif',
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <IconHeart size={36} color="#ef4444" fill="#ef4444" />
            </div>
            <p style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginTop: 8, letterSpacing: -0.5 }}>{PRODUCT}</p>
          </Link>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
            {isRegister ? 'Empezá gratis — sin tarjeta' : 'Bienvenido de vuelta'}
          </p>
        </div>

        {/* ── LOGIN ── */}
        {!isRegister && (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <input type="email"    placeholder="Email"      value={form.email}    onChange={set('email')}    required style={inputStyle} />
            <div style={{ position: 'relative' }}>
              <input type={showPw ? 'text' : 'password'} placeholder="Contraseña" value={form.password} onChange={set('password')} required minLength={1} style={{ ...inputStyle, paddingRight: 48 }} />
              <button type="button" onClick={() => setShowPw(v => !v)} aria-label={showPw ? 'Ocultar contraseña' : 'Ver contraseña'} style={eyeBtn}>
                {showPw ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            {error && (
              <div style={{ background: 'rgba(255,59,48,0.12)', border: '1px solid rgba(255,59,48,0.3)', borderRadius: 10, padding: '10px 14px' }}>
                <p style={{ fontSize: 13, color: '#ff3b30' }}>{error}</p>
              </div>
            )}
            <button type="submit" disabled={loading} style={btnPrimary(loading)}>
              {loading ? 'Cargando…' : 'Ingresar'}
            </button>
            <p style={{ textAlign: 'center', fontSize: 13, marginTop: 2 }}>
              <Link to="/recuperar" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>¿Olvidaste tu contraseña?</Link>
            </p>
          </form>
        )}

        {/* ── REGISTER · paso 1: datos ── */}
        {isRegister && regStep === 'form' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            <input type="text"     placeholder="Tu nombre"           value={form.name}  onChange={set('name')}  required style={inputStyle} />
            <input type="email"    placeholder="Email"               value={form.email} onChange={set('email')} required style={inputStyle} />
            <div style={{ position: 'relative' }}>
              <input type={showPw ? 'text' : 'password'} placeholder="Contraseña (mín. 8)" value={form.password} onChange={set('password')} required minLength={8} style={{ ...inputStyle, paddingRight: 48 }} />
              <button type="button" onClick={() => setShowPw(v => !v)} aria-label={showPw ? 'Ocultar contraseña' : 'Ver contraseña'} style={eyeBtn}>
                {showPw ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            <input type="tel"      placeholder="Teléfono (opcional)" value={form.phone} onChange={set('phone')} style={inputStyle} />

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)}
                style={{ marginTop: 3, accentColor: '#ff3b30', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>
                Acepto los{' '}
                <a href="/terminos" target="_blank" rel="noopener noreferrer" style={{ color: '#ff3b30', textDecoration: 'none' }}>Términos y Condiciones</a>
                {'. '}Tus datos son usados únicamente para mejorar la plataforma y nunca se comparten con terceros.
              </span>
            </label>

            {error && (
              <div style={{ background: 'rgba(255,59,48,0.12)', border: '1px solid rgba(255,59,48,0.3)', borderRadius: 10, padding: '10px 14px' }}>
                <p style={{ fontSize: 13, color: '#ff3b30' }}>{error}</p>
              </div>
            )}

            <button onClick={goChoosePlan} style={btnPrimary(false)}>
              Continuar → elegir plan
            </button>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0' }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>o sin registrarse</span>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
            </div>

            {/* Instant demo — fake data, no account */}
            <button onClick={startDemo} disabled={loadingDemo} style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.5)', fontSize: 14, fontWeight: 500,
              borderRadius: 12, padding: '13px', cursor: loadingDemo ? 'default' : 'pointer',
              fontFamily: 'inherit', opacity: loadingDemo ? 0.6 : 1,
            }}>
              {loadingDemo ? 'Cargando…' : 'Ver demo con datos simulados'}
            </button>
          </div>
        )}

        {/* ── REGISTER · paso 2: elegir plan ── */}
        {isRegister && regStep === 'choose' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 17, fontWeight: 800, color: '#fff', margin: 0 }}>Elegí tu plan</p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', margin: '4px 0 0' }}>
                Podés empezar gratis y pasarte a premium cuando quieras.
              </p>
            </div>

            {error && (
              <div style={{ background: 'rgba(255,59,48,0.12)', border: '1px solid rgba(255,59,48,0.3)', borderRadius: 10, padding: '10px 14px' }}>
                <p style={{ fontSize: 13, color: '#ff3b30' }}>{error}</p>
              </div>
            )}

            <PlanCards mode="register" onSelect={choosePlan} loadingPlan={loadingPlan} />

            {/* Trial — cuenta real, acceso completo 3 días */}
            <button onClick={startTrial} disabled={loadingTrial} style={{
              background: 'transparent', border: '1px dashed rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.65)',
              fontSize: 13.5, fontWeight: 600, borderRadius: 12, padding: '12px', cursor: 'pointer', fontFamily: 'inherit',
            }}>
              {loadingTrial ? 'Creando cuenta…' : 'Probar TODO gratis por 3 días'}
            </button>

            <button onClick={() => { setError(null); setRegStep('form') }} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: 13,
              cursor: 'pointer', fontFamily: 'inherit', padding: 4,
            }}>
              ← Volver a mis datos
            </button>
          </div>
        )}

        {/* Switch mode */}
        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>
          {isRegister ? '¿Ya tenés cuenta? ' : '¿No tenés cuenta? '}
          <Link to={isRegister ? '/login' : '/register'} style={{ color: '#ff3b30', textDecoration: 'none', fontWeight: 600 }}>
            {isRegister ? 'Ingresá' : 'Creá una gratis'}
          </Link>
        </p>
        <p style={{ textAlign: 'center', marginTop: 8, fontSize: 13, color: 'rgba(255,255,255,0.2)' }}>
          <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>← Volver al inicio</Link>
        </p>
      </div>
    </div>
  )
}
